import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

const WA_NUMBER = "+393273839782";

const CASES = [
  { id: 1, label: "Cambio posizione", icon: "📍", urgent: false },
  { id: 2, label: "Cambio numero", icon: "📞", urgent: false },
  { id: 3, label: "Cambio ragione sociale o nome", icon: "🏷", urgent: false },
  { id: 4, label: "Nuova brand identity", icon: "🎨", urgent: false },
  { id: 5, label: "Foto sferiche 360° di Google non ci rappresentano", icon: "📷", urgent: false },
  { id: 6, label: "Profilo sospeso", icon: "⚠", urgent: false },
  { id: 7, label: "Rivendica attivita'", icon: "🔑", urgent: false },
  { id: 8, label: "Gestione o proprieta'?", icon: "👤", urgent: false },
  { id: 9, label: "Migrazione responsabilita'", icon: "🔄", urgent: false },
  { id: 10, label: "Accorpamento 2 schede duplicate", icon: "📋", urgent: false },
  { id: 11, label: "Segnalazione urgente", icon: "🚨", urgent: true },
];

export default function WhatsAppFloatingMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    // Delay listener so the opening click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [open]);

  const handleSelect = (c) => {
    const text = encodeURIComponent(
      `Ciao Riccardo, ti contatto da schedabusiness.com.\n\nHo bisogno di assistenza per:\n*${c.label}*\n\nGrazie`
    );
    window.open(`https://wa.me/${WA_NUMBER}?text=${text}`, "_blank");
    setOpen(false);
  };

  return (
    <div ref={menuRef} className="fixed bottom-6 right-[4rem] z-50" data-testid="whatsapp-floating-menu">
      {/* Menu popup */}
      {open && (
        <div
          className="absolute bottom-20 right-0 w-[340px] sm:w-[380px] rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.7)] animate-in slide-in-from-bottom-4 duration-200"
          style={{ background: "linear-gradient(180deg, #111 0%, #0a0a0a 100%)" }}
          data-testid="whatsapp-menu-panel"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <svg viewBox="0 0 32 32" className="w-6 h-6" fill="none">
                  <path d="M16.004 2.667A13.26 13.26 0 0 0 2.74 15.93a13.16 13.16 0 0 0 1.782 6.614L2.667 29.333l6.98-1.83A13.27 13.27 0 0 0 16.004 29.333 13.27 13.27 0 0 0 29.333 16.067 13.27 13.27 0 0 0 16.004 2.667Zm0 24.266a10.87 10.87 0 0 1-5.544-1.518l-.398-.236-4.12 1.08 1.1-4.016-.26-.413a10.83 10.83 0 0 1-1.663-5.763A10.87 10.87 0 0 1 16.004 5.067 10.87 10.87 0 0 1 26.933 16.067a10.87 10.87 0 0 1-10.929 10.866Zm5.973-8.142c-.328-.164-1.937-.956-2.237-1.064-.3-.109-.519-.164-.737.164-.219.328-.847 1.064-1.038 1.283-.191.218-.382.245-.71.082-.328-.164-1.384-.51-2.637-1.627-.974-.868-1.632-1.942-1.823-2.27-.191-.328-.02-.505.144-.668.147-.147.328-.382.491-.573.164-.191.219-.328.328-.546.109-.219.055-.41-.027-.573-.082-.164-.737-1.778-1.01-2.434-.266-.64-.537-.553-.737-.564-.191-.009-.41-.011-.628-.011a1.206 1.206 0 0 0-.874.41c-.3.328-1.146 1.12-1.146 2.733 0 1.613 1.174 3.172 1.338 3.39.164.219 2.31 3.526 5.598 4.945.782.338 1.393.54 1.87.691.785.25 1.5.214 2.065.13.63-.094 1.937-.792 2.21-1.557.274-.764.274-1.42.192-1.557-.082-.136-.3-.218-.628-.382Z" fill="white"/>
                </svg>
                <span className="text-white font-bold text-sm">Come posso aiutarti?</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-white transition-colors p-1"
                data-testid="whatsapp-menu-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed">
              Seleziona il tuo caso — si apre WhatsApp con il messaggio gia' pronto
            </p>
          </div>

          {/* Options list */}
          <div className="py-2 max-h-[400px] overflow-y-auto custom-scrollbar">
            {CASES.map((c) => (
              <button
                key={c.id}
                onClick={() => handleSelect(c)}
                className={`w-full text-left px-5 py-3 flex items-center gap-3 transition-colors ${
                  c.urgent
                    ? "hover:bg-red-950/40 border-t border-white/5 mt-1"
                    : "hover:bg-white/[0.04]"
                }`}
                data-testid={`whatsapp-option-${c.id}`}
              >
                <span className="text-lg flex-shrink-0 w-7 text-center">{c.icon}</span>
                <span
                  className={`text-sm leading-tight ${
                    c.urgent ? "text-red-400 font-black" : "text-gray-300 font-medium"
                  }`}
                >
                  {c.label}
                </span>
                {c.urgent && (
                  <span className="ml-auto flex-shrink-0 bg-red-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider animate-pulse">
                    Urgente
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Footer guarantee */}
          <div className="px-5 py-3 border-t border-white/10 bg-black/40">
            <p className="text-[10px] text-gray-500 text-center leading-relaxed">
              <span className="text-red-400 font-bold">*</span> Priorita' massima — <strong className="text-white">24 ore di attesa al massimo</strong> — soddisfatto o rimborsato
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          open ? "bg-[#1a1a1a] rotate-90" : "bg-black hover:bg-[#1a1a1a]"
        }`}
        style={{
          boxShadow: open
            ? "0 4px 24px rgba(0,0,0,0.6)"
            : "0 0 0 3px #25D366, 0 4px 24px rgba(0,0,0,0.6)",
        }}
        data-testid="floating-whatsapp-btn"
        aria-label="Contattaci su WhatsApp"
      >
        {open ? (
          <X className="w-7 h-7 text-white" />
        ) : (
          <svg viewBox="0 0 32 32" className="w-9 h-9" fill="none">
            <path d="M16.004 2.667A13.26 13.26 0 0 0 2.74 15.93a13.16 13.16 0 0 0 1.782 6.614L2.667 29.333l6.98-1.83A13.27 13.27 0 0 0 16.004 29.333 13.27 13.27 0 0 0 29.333 16.067 13.27 13.27 0 0 0 16.004 2.667Zm0 24.266a10.87 10.87 0 0 1-5.544-1.518l-.398-.236-4.12 1.08 1.1-4.016-.26-.413a10.83 10.83 0 0 1-1.663-5.763A10.87 10.87 0 0 1 16.004 5.067 10.87 10.87 0 0 1 26.933 16.067a10.87 10.87 0 0 1-10.929 10.866Zm5.973-8.142c-.328-.164-1.937-.956-2.237-1.064-.3-.109-.519-.164-.737.164-.219.328-.847 1.064-1.038 1.283-.191.218-.382.245-.71.082-.328-.164-1.384-.51-2.637-1.627-.974-.868-1.632-1.942-1.823-2.27-.191-.328-.02-.505.144-.668.147-.147.328-.382.491-.573.164-.191.219-.328.328-.546.109-.219.055-.41-.027-.573-.082-.164-.737-1.778-1.01-2.434-.266-.64-.537-.553-.737-.564-.191-.009-.41-.011-.628-.011a1.206 1.206 0 0 0-.874.41c-.3.328-1.146 1.12-1.146 2.733 0 1.613 1.174 3.172 1.338 3.39.164.219 2.31 3.526 5.598 4.945.782.338 1.393.54 1.87.691.785.25 1.5.214 2.065.13.63-.094 1.937-.792 2.21-1.557.274-.764.274-1.42.192-1.557-.082-.136-.3-.218-.628-.382Z" fill="white"/>
          </svg>
        )}
      </button>
    </div>
  );
}
